import { createContext, ReactNode, useContext, useMemo, useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { ICourtViewModel } from 'viewModels';
import { CompanyController } from '../controller/CompanyController';
import { useAuth } from './auth';

type ProviderProps = {
  children: ReactNode;
};

type CourtsContextProps = {
  courts: ICourtViewModel[];
  setCourts: React.Dispatch<React.SetStateAction<ICourtViewModel[]>>;
  isLoadingCourts: boolean;
  setDistrictSelectedId: React.Dispatch<React.SetStateAction<string>>;
  districtSelectedId: string;
  courtSelectedId: string;
  setCourtSelectedId: React.Dispatch<React.SetStateAction<string>>;
  lastAddedCourtId: string;
  setLastAddedCourtId: React.Dispatch<React.SetStateAction<string>>;
};

const CourtsContext = createContext<CourtsContextProps>({} as CourtsContextProps);

const CourtsProvider = ({ children }: ProviderProps) => {
  const { tokenIsValid } = useAuth();
  const [courts, setCourts] = useState<ICourtViewModel[]>([] as ICourtViewModel[]);
  const [districtSelectedId, setDistrictSelectedId] = useState('');
  const [courtSelectedId, setCourtSelectedId] = useState('');
  const [lastAddedCourtId, setLastAddedCourtId] = useState('');

  const { isLoading: isLoadingCourts } = useQuery(
    ['courts', districtSelectedId],
    () => CompanyController.getCourtsByDistrictSelected(districtSelectedId),
    {
      onSuccess: (data) => {
        setCourts(data.sort((a, b) => a.name.localeCompare(b.name)));
      },
      refetchOnWindowFocus: false,
      enabled: tokenIsValid && districtSelectedId !== '',
    },
  );

  useEffect(() => {
    if (!tokenIsValid) {
      setCourts([]);
      setDistrictSelectedId('');
      setCourtSelectedId('');
      setLastAddedCourtId('');
    }
  }, [tokenIsValid]);

  const contextValues = useMemo(
    () => ({
      courts,
      setCourts,
      isLoadingCourts,
      setDistrictSelectedId,
      districtSelectedId,
      courtSelectedId,
      setCourtSelectedId,
      lastAddedCourtId,
      setLastAddedCourtId,
    }),
    [courts, isLoadingCourts, districtSelectedId, courtSelectedId, lastAddedCourtId],
  );
  return <CourtsContext.Provider value={contextValues}>{children}</CourtsContext.Provider>;
};

export default CourtsProvider;

export const useCourts: () => CourtsContextProps = () => useContext(CourtsContext);
